import React, { useState } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Skeleton,
    Button,
    IconButton,
    alpha,
    Chip,
    Checkbox,
    Drawer
} from '@mui/material'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import TuneIcon from '@mui/icons-material/Tune'

import { AllWalletTabType, BrandBalance, DomainBalance, FetchControl } from '@/src/types/wallet'
import { WALLET_TABS } from '@/src/constants/wallet'
import { getDaysUntilExpiry, formatDisplayDate } from '@/src/utils/wallet'
import WalletEmptyStateWrapper from '@/src/components/common/wallet/walletEmptyState'
import DomainBalanceCarouselWrapper from '@/src/components/common/wallet/domainBalanceCarousel'
import { CleartripTransactions } from '@/src/components/common/wallet/cleartripTransactions'
import type { CleartripFormattedTransaction } from '@/src/components/common/wallet/cleartripTransactions/cleartripTransactions'

export interface FormattedTransaction {
    id: string
    source: string
    reference: string | null
    amount: number
    currency: string
    expiryDate: string
    transactionDate: string
    rawSourceType: string
    iconType: 'refresh' | 'gift' | 'card'
    txnType?: string
}

interface WalletPageProps {
    // Brand — used only for conditionally hiding info icon and Flyin transactions section
    isCleartrip: boolean

    // Balance data
    displayBalance: number
    displayCurrency: string
    domainBalances: DomainBalance[]
    hasMultipleDomainBalances: boolean

    brandBalances: BrandBalance[]

    // Transactions data
    formattedTransactions: FormattedTransaction[]
    cleartripFallbackTransactions?: CleartripFormattedTransaction[]
    cleartripTransactionLabels?: {
        transactionHistoryLabel: string
        noTransactionsLabel: string
        loadMoreLabel: string
        loadingLabel: string
        transactionIdLabel: string
    }
    isRtl?: boolean
    hasMoreTransactions: boolean

    // Tab state
    activeTab: AllWalletTabType
    onTabChange: (tab: AllWalletTabType) => void

    brandFilter: FetchControl
    onBrandFilterChange: (filter: FetchControl) => void

    // Loading states
    isLoadingBalance: boolean
    isLoadingTransactions: boolean
    isFetchingMore: boolean

    // Error states
    balanceError: string | null
    transactionsError: string | null

    // Actions
    onLoadMore: () => void
    onRefresh: () => void
    onGoToHome?: () => void
    showBackButton?: boolean

    // FAQ drawer
    isFAQDrawerOpen?: boolean
    onOpenFAQDrawer?: () => void
    onCloseFAQDrawer?: () => void

    // Formatting
    formatAmount: (amount: number, currency: string) => string

    // Translations
    t: (key: string, values?: Record<string, any>) => string
    tCommon: (key: string) => string
    aboutWalletLabel: string

    // Locale
    locale: string

    // AB-driven multi-brand toggle
    showMultiBrandWallet: boolean
}

const WalletPage: React.FC<WalletPageProps> = ({
    isCleartrip,
    displayBalance,
    displayCurrency,
    domainBalances,
    hasMultipleDomainBalances,
    brandBalances,
    formattedTransactions,
    cleartripFallbackTransactions = [],
    cleartripTransactionLabels,
    isRtl = false,
    hasMoreTransactions,
    activeTab,
    onTabChange,
    brandFilter,
    onBrandFilterChange,
    isLoadingBalance,
    isLoadingTransactions,
    isFetchingMore,
    balanceError,
    transactionsError,
    onLoadMore,
    onRefresh,
    onGoToHome,
    showBackButton = false,
    isFAQDrawerOpen = false,
    onOpenFAQDrawer,
    onCloseFAQDrawer,
    formatAmount,
    t,
    tCommon,
    aboutWalletLabel,
    locale,
    showMultiBrandWallet
}) => {
    const shouldShowBalanceCarousel = brandBalances.length > 0 || domainBalances.length > 0
    const componentRadius = '14px'
    const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false)
    const [pendingFilter, setPendingFilter] = useState<FetchControl>('MAIN')

    const getIconComponent = (iconType: 'refresh' | 'gift' | 'card') => {
        switch (iconType) {
            case 'refresh':
                return RefreshIcon
            case 'gift':
                return CardGiftcardOutlinedIcon
            case 'card':
            default:
                return CreditCardOutlinedIcon
        }
    }

    const getSingleFilterFromControl = (control: FetchControl): FetchControl =>
        control === 'LINKED' ? 'LINKED' : 'MAIN'

    const isCleartripFilterOnly = showMultiBrandWallet && brandFilter === 'LINKED'

    const handleOpenFilterSheet = () => {
        setPendingFilter(getSingleFilterFromControl(brandFilter))
        setIsFilterSheetOpen(true)
    }

    const handleCloseFilterSheet = () => {
        setIsFilterSheetOpen(false)
    }

    const handleApplyFilter = () => {
        onBrandFilterChange(pendingFilter)
        setIsFilterSheetOpen(false)
    }

    return (
        <Box
            sx={{
                bgcolor: 'grey.100',
                ...(!isCleartrip && { pb: 10 }) // Space for bottom toolbar (Flyin only)
            }}
        >
            {/* Page Title */}
            <Box
                sx={{
                    px: 2,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}
            >
                {showBackButton && onGoToHome && (
                    <IconButton
                        onClick={onGoToHome}
                        size="small"
                        sx={{
                            position: 'absolute',
                            left: 8,
                            color: 'primary.main'
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                )}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('title')}
                </Typography>
            </Box>

            {shouldShowBalanceCarousel ? (
                <Box sx={{ pt: 1 }}>
                    <DomainBalanceCarouselWrapper
                        displayBalance={displayBalance}
                        displayCurrency={displayCurrency}
                        domainBalances={domainBalances}
                        brandBalances={brandBalances}
                        formatAmount={formatAmount}
                        t={t}
                        attachBottom={!!onOpenFAQDrawer}
                    />
                </Box>
            ) : (
                <Box sx={{ px: 2 }}>
                    <Card
                        elevation={0}
                        sx={{
                            background: 'white',
                            color: 'text.primary',
                            borderRadius: componentRadius,
                            border: 1,
                            borderColor: 'divider'
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <AccountBalanceWalletIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    {t('walletBalance')}
                                </Typography>
                            </Box>

                            {isLoadingBalance ? (
                                <Skeleton variant="text" width={150} height={48} sx={{ bgcolor: 'grey.200' }} />
                            ) : balanceError ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {balanceError}
                                </Typography>
                            ) : (
                                <>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontSize: '1.8rem',
                                            fontWeight: 700,
                                            letterSpacing: '-0.5px',
                                            mb: 1,
                                            color: 'text.primary'
                                        }}
                                    >
                                        {formatAmount(displayBalance, displayCurrency)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.75rem',
                                            color: 'text.secondary',
                                            display: 'block'
                                        }}
                                    >
                                        {t('usableOnlyInCurrency', { currency: displayCurrency })}
                                    </Typography>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* About Wallet Block */}
            {onOpenFAQDrawer && (
                <Box
                    onClick={onOpenFAQDrawer}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mx: 2,
                        mt: shouldShowBalanceCarousel ? 0 : 1.5,
                        px: 2,
                        py: 1.5,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderRadius: componentRadius,
                        borderTopLeftRadius: shouldShowBalanceCarousel ? 0 : undefined,
                        borderTopRightRadius: shouldShowBalanceCarousel ? 0 : undefined,
                        borderTop: shouldShowBalanceCarousel ? 0 : 1,
                        borderColor: 'divider',
                        cursor: 'pointer',
                        '&:hover': {
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'grey.200',
                            color: 'text.secondary',
                            mr: 1.5
                        }}
                    >
                        <HelpOutlineIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            flex: 1,
                            fontWeight: 600,
                            color: 'text.primary'
                        }}
                    >
                        {aboutWalletLabel}
                    </Typography>
                    <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
                </Box>
            )}

            {/* Flyin Wallet Activity, Tabs, and Transactions — hidden for Cleartrip (uses separate component) */}
            {!isCleartrip && (
                <>
                    <Card
                        elevation={0}
                        sx={{
                            mx: 0,
                            width: '100%',
                            mt: 2,
                            borderRadius: 0,
                            border: 1,
                            borderLeft: 0,
                            borderRight: 0,
                            borderColor: 'divider',
                            bgcolor: 'background.paper'
                        }}
                    >
                        <Box
                            sx={{
                                px: 2,
                                pt: 2.5,
                                pb: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    fontSize: '1.125rem'
                                }}
                            >
                                {t('walletActivity')}
                            </Typography>
                            {showMultiBrandWallet && (
                                <IconButton
                                    size="small"
                                    onClick={handleOpenFilterSheet}
                                    sx={{
                                        border: 1,
                                        borderColor: 'divider',
                                        bgcolor: 'background.paper'
                                    }}
                                >
                                    <TuneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </IconButton>
                            )}
                        </Box>
                        {!isCleartripFilterOnly && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    px: 2,
                                    py: 0.5,
                                    bgcolor: 'background.paper'
                                }}
                            >
                                {WALLET_TABS.map(tab => (
                                    <Button
                                        key={tab}
                                        variant={activeTab === tab ? 'contained' : 'text'}
                                        size="small"
                                        onClick={() => onTabChange(tab)}
                                        sx={{
                                            borderRadius: 2,
                                            px: 2,
                                            py: 0.75,
                                            fontWeight: activeTab === tab ? 600 : 500,
                                            textTransform: 'capitalize',
                                            bgcolor: activeTab === tab ? 'primary.main' : 'transparent',
                                            color: activeTab === tab ? 'white' : 'text.secondary',
                                            '&:hover': {
                                                bgcolor:
                                                    activeTab === tab
                                                        ? 'primary.dark'
                                                        : theme => alpha(theme.palette.primary.main, 0.08)
                                            }
                                        }}
                                    >
                                        {t(`tabs.${tab.toLowerCase()}`)}
                                    </Button>
                                ))}
                            </Box>
                        )}

                        {/* Transactions List */}
                        <Box sx={{ px: 2, pt: 2 }}>
                            {isLoadingTransactions && !isFetchingMore ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <Card
                                        key={i}
                                        elevation={0}
                                        sx={{
                                            mb: 1.5,
                                            bgcolor: 'background.paper',
                                            borderRadius: componentRadius,
                                            border: 1,
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 1.75 } }}>
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                                <Skeleton
                                                    variant="circular"
                                                    width={48}
                                                    height={48}
                                                    sx={{ bgcolor: 'grey.200', flexShrink: 0 }}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                                    <Skeleton
                                                        variant="text"
                                                        width="70%"
                                                        height={20}
                                                        sx={{ mb: 0.75 }}
                                                    />
                                                    <Skeleton variant="text" width="50%" height={16} />
                                                </Box>
                                                <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.5 }}>
                                                    <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
                                                    <Skeleton
                                                        variant="rectangular"
                                                        width={100}
                                                        height={22}
                                                        sx={{ borderRadius: '12px', ml: 'auto' }}
                                                    />
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))
                            ) : transactionsError ? (
                                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                                    <Typography variant="body2">{transactionsError}</Typography>
                                    <Button variant="text" size="small" onClick={onRefresh} sx={{ mt: 1 }}>
                                        {tCommon('retry')}
                                    </Button>
                                </Box>
                            ) : isCleartripFilterOnly && cleartripFallbackTransactions.length === 0 ? (
                                <WalletEmptyStateWrapper />
                            ) : isCleartripFilterOnly ? (
                                <CleartripTransactions
                                    transactions={cleartripFallbackTransactions}
                                    hasMoreTransactions={hasMoreTransactions}
                                    isFetchingMore={isFetchingMore}
                                    onLoadMore={onLoadMore}
                                    formatAmount={formatAmount}
                                    transactionHistoryLabel={
                                        cleartripTransactionLabels?.transactionHistoryLabel || t('transactionHistory')
                                    }
                                    noTransactionsLabel={
                                        cleartripTransactionLabels?.noTransactionsLabel || t('noTransactions')
                                    }
                                    loadMoreLabel={cleartripTransactionLabels?.loadMoreLabel || t('loadMore')}
                                    loadingLabel={cleartripTransactionLabels?.loadingLabel || t('loading')}
                                    transactionIdLabel={cleartripTransactionLabels?.transactionIdLabel || t('transactionId')}
                                    isRtl={isRtl}
                                    showHeader={false}
                                />
                            ) : formattedTransactions.length === 0 ? (
                                <WalletEmptyStateWrapper />
                            ) : (
                                <>
                                    {formattedTransactions.map(txn => {
                                        const daysUntilExpiry =
                                            activeTab !== 'USED' ? getDaysUntilExpiry(txn.expiryDate) : null
                                        const displayDate = formatDisplayDate(txn.transactionDate, locale)
                                        const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 10

                                        return (
                                            <Card
                                                key={txn.id}
                                                elevation={0}
                                                sx={{
                                                    mb: 1.5,
                                                    bgcolor: 'background.paper',
                                                    borderRadius: componentRadius,
                                                    border: 1,
                                                    borderColor: 'divider'
                                                }}
                                            >
                                                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 1.75 } }}>
                                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                                        {(() => {
                                                            const IconComponent = getIconComponent(txn.iconType)
                                                            return (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: 48,
                                                                        height: 48,
                                                                        borderRadius: '50%',
                                                                        bgcolor: theme =>
                                                                            alpha(theme.palette.primary.main, 0.08),
                                                                        flexShrink: 0
                                                                    }}
                                                                >
                                                                    <IconComponent
                                                                        sx={{ fontSize: 28, color: 'primary.main' }}
                                                                    />
                                                                </Box>
                                                            )
                                                        })()}

                                                        <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    mb: 0.75
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        fontSize: '0.9375rem',
                                                                        lineHeight: 1.4,
                                                                        color: 'text.primary'
                                                                    }}
                                                                >
                                                                    {txn.source?.trim() || '-'}
                                                                </Typography>
                                                            </Box>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.8125rem',
                                                                    display: 'block'
                                                                }}
                                                            >
                                                                {displayDate}
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.5 }}>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color:
                                                                        activeTab === 'EARNED' || activeTab === 'REFUND'
                                                                            ? 'success.main'
                                                                            : 'error.main',
                                                                    fontSize: '1rem',
                                                                    mb: 1,
                                                                    lineHeight: 1.2
                                                                }}
                                                            >
                                                                {activeTab === 'EARNED' || activeTab === 'REFUND'
                                                                    ? '+'
                                                                    : '-'}
                                                                {formatAmount(txn.amount, txn.currency)}
                                                            </Typography>

                                                            {activeTab !== 'USED' &&
                                                                txn.expiryDate &&
                                                                txn.expiryDate !== '-' && (
                                                                    <Chip
                                                                        label={
                                                                            daysUntilExpiry !== null &&
                                                                            daysUntilExpiry <= 10
                                                                                ? daysUntilExpiry === 1
                                                                                    ? t('expiresInDays', {
                                                                                          days: daysUntilExpiry
                                                                                      })
                                                                                    : t('expiresInDaysPlural', {
                                                                                          days: daysUntilExpiry
                                                                                      })
                                                                                : t('validTill', {
                                                                                      date: formatDisplayDate(txn.expiryDate, locale)
                                                                                  })
                                                                        }
                                                                        size="small"
                                                                        sx={{
                                                                            height: 22,
                                                                            fontSize: '0.6875rem',
                                                                            fontWeight: 500,
                                                                            bgcolor: isExpiringSoon
                                                                                ? theme =>
                                                                                      alpha(
                                                                                          theme.palette.warning.main,
                                                                                          0.15
                                                                                      )
                                                                                : 'grey.200',
                                                                            color: isExpiringSoon
                                                                                ? 'warning.main'
                                                                                : 'text.secondary',
                                                                            borderRadius: '12px',
                                                                            '& .MuiChip-label': { px: 1.25, py: 0.25 }
                                                                        }}
                                                                    />
                                                                )}
                                                        </Box>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            mt: 1.5,
                                                            pt: 1.5,
                                                            borderTop: 1,
                                                            borderColor: 'divider',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        {txn.reference && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}
                                                            >
                                                                {txn.reference}
                                                            </Typography>
                                                        )}
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'text.disabled',
                                                                fontSize: '0.6875rem',
                                                                fontFamily: 'monospace',
                                                                ml: 'auto'
                                                            }}
                                                        >
                                                            {t('transactionId')}: {txn.id}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        )
                                    })}

                                    {hasMoreTransactions && (
                                        <Box sx={{ textAlign: 'center', pt: 2, pb: 4 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={onLoadMore}
                                                disabled={isFetchingMore}
                                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                                            >
                                                {isFetchingMore ? t('loading') : t('loadMore')}
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    </Card>
                    <Drawer anchor="bottom" open={isFilterSheetOpen} onClose={handleCloseFilterSheet}>
                        <Box
                            sx={{
                                px: 3,
                                pt: 1.5,
                                pb: 2.5,
                                borderTopLeftRadius: 4,
                                borderTopRightRadius: 4,
                                bgcolor: 'background.paper'
                            }}
                        >
                            <Box
                                sx={{
                                    width: 44,
                                    height: 5,
                                    borderRadius: 10,
                                    bgcolor: 'grey.400',
                                    mx: 'auto',
                                    mb: 2.5
                                }}
                            />
                            <Typography
                                variant="h6"
                                sx={{ textAlign: 'center', fontWeight: 600, fontSize: '1.75rem', mb: 2.5 }}
                            >
                                {t('filtersTitle')}
                            </Typography>
                            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2.5 }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 600, fontSize: '1.125rem', mb: 1.75 }}
                                >
                                    {t('paidVia')}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 1.5
                                    }}
                                >
                                    <Checkbox
                                        checked={pendingFilter === 'MAIN'}
                                        onChange={() => setPendingFilter('MAIN')}
                                        sx={{ p: 0.5, mr: 1.25 }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 400, fontSize: '1rem', color: 'text.secondary' }}
                                    >
                                        {t('brandFilters.flyinWallet')}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 0.5
                                    }}
                                >
                                    <Checkbox
                                        checked={pendingFilter === 'LINKED'}
                                        onChange={() => setPendingFilter('LINKED')}
                                        sx={{ p: 0.5, mr: 1.25 }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 400, fontSize: '1rem', color: 'text.secondary' }}
                                    >
                                        {t('brandFilters.cleartripWallet')}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    mt: 2.5,
                                    pt: 2.25,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 1.5
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={() => setPendingFilter('MAIN')}
                                    sx={{
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        minHeight: 52
                                    }}
                                >
                                    {t('clearAll')}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleApplyFilter}
                                    sx={{
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        minHeight: 52
                                    }}
                                >
                                    {tCommon('apply')}
                                </Button>
                            </Box>
                        </Box>
                    </Drawer>
                </>
            )}
        </Box>
    )
}

export { WalletPage }
export type { WalletPageProps }
